import { useParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { NavRow, RowGroup } from "../../components/Rows";
import { dayLabel, todayStr } from "../../domain/date";
import { aboutThem, familyAnswerGroups, familyTraining, pronouns, subjectOf } from "../../domain/family";
import { latestTests } from "../../domain/fitnessTests";
import { stillRight } from "../../domain/trainingQuestions";
import type { DayTests } from "../../domain/types";
import { useLinked } from "../../lib/linked";
import { useGo } from "../../lib/useGo";
import { LinkedShell, familyPath, setByWords, useMyName } from "./linkedPage";

/** "9 stands, tandem 6 s" */
function testWords(t: DayTests): string {
  const parts: string[] = [];
  if (typeof t.chairStand === "number") parts.push(`${t.chairStand} stands`);
  if (typeof t.balanceStage === "number")
    parts.push(
      t.balanceStage >= 3 ? "tandem 10 s" : typeof t.balanceSecs === "number" ? `tandem ${t.balanceSecs} s` : "tandem not held"
    );
  if (typeof t.tug === "number") parts.push(`up-and-go ${t.tug} s`);
  if (typeof t.step === "number") parts.push(`${t.step} steps in 2 minutes`);
  return parts.join(", ");
}

/**
 * Their exercise answers, read back about them (board FamilyTrainingAnswers; ADR-056), each group
 * with a Change that opens Plan my exercise in their name at that question. The answers food
 * shares are shown once and changed on their eating answers. The safety screens are only ever on
 * their phone, so this page says so and nothing more; the tests are theirs to do, or counts the
 * reader timed beside them and typed.
 */
export default function FamilyTrainingAnswers() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  const me = useMyName();
  const go = useGo();
  const today = todayStr();

  return (
    <LinkedShell
      q={q}
      title={(name) => (name ? `${name}’s exercise answers` : "Exercise answers")}
      back={{ to: familyPath(id, "training"), label: "Back" }}
    >
      {({ S, name }) => {
        const p = pronouns(S);
        const f = familyTraining(S, today);
        const groups = familyAnswerGroups(S, p);
        const shared = stillRight(S);
        const lt = latestTests(S, today);
        const by = setByWords(f.setBy, me);
        const change = (screen: string) => go(familyPath(id, `exercise/${screen}`));

        if (!f.answeredOn)
          return (
            <Hero
              label="Exercise answers"
              title="Not answered yet"
              line={`Answer them with ${p.them}, or ask ${p.them} to on ${p.their} phone.`}
              actions={
                <Button grow onClick={() => change("start")}>
                  Answer for {p.them}
                </Button>
              }
            />
          );

        return (
          <>
            <p className="you-intro">
              {by ? `${by[0].toUpperCase()}${by.slice(1)}` : `Answered on ${p.their} phone`}, {dayLabel(f.answeredOn)}. You can
              change these; {subjectOf(p)} {subjectOf(p) === "they" ? "see" : "sees"} “set by you” beside a change.
            </p>

            <RowGroup heading={`${p.Their} answers`}>
              {groups.map((g) => (
                <NavRow
                  key={g.screen}
                  kind="action"
                  title={g.text}
                  action={
                    <Button variant="text" onClick={() => change(g.screen)} ariaLabel={`Change: ${g.text}`}>
                      Change
                    </Button>
                  }
                />
              ))}
            </RowGroup>

            {shared.length ? (
              <RowGroup heading="From Plan my food">
                {shared.map((r) => (
                  <NavRow key={r.key} kind="value" title={aboutThem(r.text, p)} />
                ))}
                <NavRow
                  icon={I.list}
                  title="Eating answers"
                  subtitle="Change these there; both plans read them"
                  to={familyPath(id, "answers")}
                />
              </RowGroup>
            ) : null}

            <RowGroup heading={`Only on ${p.their} phone`}>
              <NavRow kind="value" icon={I.lock} title="Before you start" subtitle="Kept there, never shown here" />
              <NavRow kind="value" icon={I.lock} title="Anything recent" subtitle="Kept there, never shown here" />
              <NavRow
                icon={I.legs}
                title="The tests"
                subtitle={
                  lt
                    ? `${lt.tests.by ? setByWords(lt.tests.by, me)?.replace("set by", "typed by") : `on ${p.their} phone`}, ${dayLabel(lt.ds)} · ${testWords(lt.tests)}`
                    : `Not yet — on ${p.their} phone, with you beside ${p.them}, or type the counts you timed`
                }
                to={familyPath(id, "exercise/tests")}
              />
            </RowGroup>

            <p className="you-fine">
              {name}’s safety answers decide whether {p.their} plan waits for a doctor. They are asked only on {p.their} own
              phone and never leave it.
            </p>
          </>
        );
      }}
    </LinkedShell>
  );
}
